<template>
  <div class="signature-templates">
    <TMSHeader
      :buttons="btnOptions"
      :needGoback="false"
      :title="updateInfo"
    />
    <div class="templates">
      <div
        class="template-detail"
        v-for="(template, index) in templates"
        :key="template.id"
      >
        <div
          class="remove"
          v-if="canEdit"
          @click="deleteTemplate(index)"
        ><i class="fa fa-times fa-lg" /></div>

        <InputField
          :label="$t('signature') + ' ' + $t('name')"
          class="small-6 medium-6 large-6"
          v-model="template.title"
          :readonly="!canEdit"
          :showColon="false"
          :required="canEdit"
          defaultEmpty
          @updateValue="val => template.title = val"
        />

        <div
          v-if="isSignatureForTenant"
          class="small-6 medium-6 large-6"
          :class="{ 'highlight-empty': highlightEmpty && emptyZones[index] }"
        >
          <label :class="['title', {'required-flag': canEdit, 'margin-top': !canEdit}]">{{ $t('eCircular.zone') }}</label>
          <HKLMultiSelect
            v-if="canEdit"
            :options="zoneOptions"
            :value="template.zones"
            placeholder=""
            :searchable="false"
            @change="val => { template.zones = val; formUtils.setDirty() }"
          />
          <p
            v-else
            class="zone"
          >{{ (template.zones || []).join('; ') }}</p>
        </div>

        <SignatureEditor
          :label="$t('signature')"
          :content="template.content"
          :updateContent="val => updateContent(val, index)"
          :isEditing="canEdit"
          :previewKey="template.id"
        />
      </div>
      <modal
        ref="cancelModal"
        :showCloseBtn="false"
        :modalId="`cancel-signature-update-${+new Date()}`"
      >
        <div slot="content">
          <div class="font-bold">{{ $t('cancelChanges') }}</div>
        </div>
        <div slot="btns">
          <button
            type="button"
            class="btn--darkblue"
            @click="closeModal"
          >{{ $t('action.no') }}</button>
          <button
            type="button"
            class="btn--orange"
            @click="cancel"
          >{{ $t('action.yesContinue') }}</button>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import Modal from '@/controls/Modal'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import InputField from '@/controls/InputField'
import SignatureEditor from './SignatureEditor'
import TextField from '@/controls/TextField'
import TMSHeader from '@/controls/TMSHeader'

import { getBuildingFloorZoneMap } from '@/api/buildings/request'
import { getTemplates, updateTemplate } from '@/api/settings/request'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import { aclHelper } from '@/utils/aclHelper'
import { templateType } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import userHelper from '@/utils/userHelper'

import { mapGetters } from 'vuex'
import lodash from 'lodash'

export default {
  name: 'signature-template',
  components: {
    InputField,
    HKLMultiSelect,
    Modal,
    SignatureEditor,
    TextField,
    TMSHeader,
  },
  data () {
    const signatureTemplates = {
      tenantSignature: 'tenant-signature-template',
      vendorSignature: 'vendor-signature-template',
    }
    return {
      formUtils,
      templates: [],
      bkTemplates: [],
      zoneOptions: [],
      highlightEmpty: false,
      canEdit: false,
      eCirucularType: '',
      signatureTemplates,
    }
  },
  async mounted () {
    await this.getEcircularTemplates()
  },
  watch: {
    async $route () {
      await this.getEcircularTemplates()
      if (this.canEdit === true) {
        this.canEdit = false
      }
    },

  },
  methods: {
    getUpdator (template) {
      if (!template.updator) return '--'
      return userHelper.getUserName(template.updator.username, template.updator.displayName)
    },
    getUpdateTime (template) {
      return dateUtil.format(template.updatedAt, 'DD MMM YY HH:mm')
    },
    enableEdit () {
      this.canEdit = true
    },
    cancelEdit () {
      if (this.isPageDirty) {
        this.$refs['cancelModal'].open()
      } else {
        this.canEdit = false
      }
    },
    closeModal () {
      this.$refs['cancelModal'].close()
    },
    updateContent (val, index) {
      this.templates[index].content = val
      this.$forceUpdate()
    },
    resetEdit () {
      this.canEdit = false
      this.highlightEmpty = false
      formUtils.resetDirty()
    },
    cancel () {
      this.templates = lodash.cloneDeep(this.bkTemplates)
      this.closeModal()
      this.resetEdit()
    },
    addTemplate () {
      this.templates.push({
        type: this.eCirucularType,
      })
      formUtils.setDirty()
    },
    deleteTemplate (index) {
      this.templates.splice(index, 1)
      formUtils.setDirty()
    },
    checkEmptyZones () {
      if (this.eCirucularType === templateType.ECIRCULAR_TENANT) {
        return this.emptyZones.some(empty => empty)
      } else {
        return false
      }
    },
    validate () {
      this.highlightEmpty = true
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.signature-templates')
      return fusionInputCheckUtil.isAllFieldsValid('.signature-templates') && !this.checkEmptyZones()
    },
    async save () {
      if (!this.validate()) return

      this.templates = await updateTemplate(this.templates, this.eCirucularType)
      this.bkTemplates = lodash.cloneDeep(this.templates)
      this.closeModal()
      this.resetEdit()
    },
    async getEcircularTemplates () {
      const routeName = this.$router.currentRoute.name
      if (routeName === this.signatureTemplates.tenantSignature) {
        this.eCirucularType = templateType.ECIRCULAR_TENANT
      }
      if (routeName === this.signatureTemplates.vendorSignature) {
        this.eCirucularType = templateType.ECIRCULAR_VENDOR
      }
      if (this.eCirucularType) {
        this.templates = await getTemplates(this.eCirucularType)
        this.bkTemplates = lodash.cloneDeep(this.templates)
        if (this.isSignatureForTenant) {
          const { Buildings } = await getBuildingFloorZoneMap()
          this.zoneOptions = Buildings
        }
      }
    },
  },
  computed: {
    ...mapGetters('form', ['isPageDirty']),

    btnOptions () {
      if (!aclHelper.getSettingsUpdateAccess()) return []

      const btns = []
      if (this.canEdit) {
        btns.push({
          title: this.$i18n.t('action.add'),
          cls: 'btn--darkblue',
          func: this.addTemplate,
        }, {
          title: this.$i18n.t('action.cancel'),
          cls: 'btn--darkblue',
          func: this.cancelEdit,
        }, {
          title: this.$i18n.t('action.save'),
          cls: 'btn--orange',
          func: this.save,
        })
      } else {
        btns.push({
          title: this.$i18n.t('action.edit'),
          cls: 'btn--orange',
          func: this.enableEdit,
        })
      }
      return btns
    },
    emptyZones () {
      let emptyZones = []
      this.templates.forEach((template, index) => {
        if (!lodash.get(template, 'zones.length')) {
          emptyZones[index] = true
        } else {
          emptyZones[index] = false
        }
      })
      return emptyZones
    },
    updateInfo () {
      if (!this.templates.length) return
      const lastUpdateItem = lodash.orderBy(this.templates, 'updatedAt', 'desc')[0]
      return this.$i18n.t('lastUpdatedBy', [this.getUpdator(lastUpdateItem), this.getUpdateTime(lastUpdateItem)])
    },
    isSignatureForTenant () {
      return this.eCirucularType === templateType.ECIRCULAR_TENANT
    },
  },
}
</script>

<style lang="scss">
.signature-templates {
  .highlight-empty {
    .hkl-select,
    .hkl-select .ant-select-selection,
    .multiselect,
    .multiselect__tags {
      background-color: #f9ecea !important;
    }
  }

  .tms-header {
    padding: 10px 15px;
    min-height: 50px!important;
    box-shadow: none;

    .title {
      font-size: 14px!important;
      font-style: italic;
      font-family: Verdana;
      font-weight: normal;
      color: #878787;
      line-height: 50px;
    }
  }

  .content {
    display: flex;

    .main-title {
      flex: 4;
    }
  }

  .templates {
    padding: 10px 15px;
    background-color: #fafbfc;

    .template-detail {
      padding: 20px 15px;
      background-color: #ffffff;
      box-shadow: 0 2px 4px 0 rgba(212, 212, 212, 0.5);

      .remove {
        float: right;
        font-size: 20px;
        :hover {
          cursor: pointer;
        }
      }

      .title {
        font-weight: bold;
      }

      .zone {
        margin-bottom: 5px;
        line-height: 2;
      }

      .margin-top {
        margin-top: 10px;
      }
    }
    .template-detail + .template-detail {
      margin-top: 20px;
    }
  }

  .hkl-select,
  .multiselect {
    width: 95%;
    .multiselect__tag {
      background: #6e7a91;
      height: 38px;
      margin-bottom: 0;
      line-height: 30px;

      .multiselect__tag-icon::after {
        color: #3a505f;
        line-height: 33px;
      }
    }
  }

  .signature .text-input textarea {
    min-height: 60px;
    overflow-y: auto;
    max-height: 250px;
  }
  .preview {
    label {
      margin-top: 5px;
      margin-bottom: 3px;
    }
    .content {
      border: dotted 1px #B5B5B5;
      min-height: 60px;
      display: flex;
      flex-direction: column;
      label {
        margin-top: 0;
        margin-bottom: 0;
      }
    }
  }
}
</style>
